/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';

export interface ColumnDef<T = Record<string, unknown>> {
  key: keyof T | string;
  header: string;
  render?: (value: unknown, row?: T) => React.ReactNode;
  align?: 'left' | 'right' | 'center';
  className?: string;
  sortable?: boolean;
}

interface DataTableProps<T = Record<string, unknown>> {
  columns: ColumnDef<T>[];
  data: T[];
  keyExtractor: (row: T, index: number) => string | number;
  isEmpty?: boolean;
  emptyMessage?: string;
  onRowClick?: (row: T) => void;
  onColumnSort?: (columnKey: string) => void;
  /** Content shown in a full-width row under a row; null for none */
  renderExpandedRow?: (row: T) => React.ReactNode;
  rowClassName?: string;
  /** Extra classes for one row, e.g. to mark the row that is open */
  getRowClassName?: (row: T) => string;
  headerClassName?: string;
}

export const DataTable = React.forwardRef<
  HTMLTableElement,
  DataTableProps<any>
>(
  (
    {
      columns,
      data,
      keyExtractor,
      isEmpty = false,
      emptyMessage = 'No data available',
      onRowClick,
      onColumnSort,
      renderExpandedRow,
      getRowClassName,
      rowClassName = 'hover:bg-gray-50 transition',
      headerClassName = 'px-4 py-3 text-left font-semibold text-gray-700',
    },
    ref
  ) => {
    const getAlignClass = (align?: 'left' | 'right' | 'center') => {
      switch (align) {
        case 'right':
          return 'text-right';
        case 'center':
          return 'text-center';
        default:
          return 'text-left';
      }
    };

    return (
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table ref={ref} className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {columns.map((column) => (
                  <th
                    key={String(column.key)}
                    className={`${headerClassName} ${getAlignClass(
                      column.align
                    )} ${column.className || ''} ${
                      column.sortable && onColumnSort
                        ? 'cursor-pointer hover:bg-gray-100 select-none'
                        : ''
                    }`}
                    onClick={() => {
                      if (column.sortable && onColumnSort) {
                        onColumnSort(String(column.key));
                      }
                    }}
                  >
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isEmpty || data.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-4 py-8 text-center text-gray-500"
                  >
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                data.map((row, index) => (
                  <React.Fragment key={keyExtractor(row, index)}>
                    <tr
                      className={`${rowClassName} ${
                        onRowClick ? 'cursor-pointer' : ''
                      } ${getRowClassName?.(row) ?? ''}`}
                      onClick={() => onRowClick?.(row)}
                    >
                      {columns.map((column) => {
                        const cellValue =
                          column.key in row
                            ? row[column.key as keyof typeof row]
                            : undefined;
                        const rendered: React.ReactNode = column.render
                          ? column.render(cellValue, row)
                          : (cellValue as React.ReactNode);

                        return (
                          <td
                            key={String(column.key)}
                            className={`px-4 py-3 text-gray-900 ${getAlignClass(
                              column.align
                            )} ${column.className || ''}`}
                          >
                            {rendered}
                          </td>
                        );
                      })}
                    </tr>
                    {renderExpandedRow?.(row) && (
                      <tr>
                        <td colSpan={columns.length} className="p-0">
                          {renderExpandedRow(row)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
);

DataTable.displayName = 'DataTable';
